import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { createFeatureHub } from '@feature-hub/core'
import { loadFederatedModule } from '@feature-hub/module-loader-federation'
import { FeatureHubContextProvider } from '@feature-hub/react'
import { RouterProvider } from 'react-router'
import { createBrowserRouter } from 'react-router-dom'
import App from 'App'
import './i18n'
import { defineStoreFeatureService } from '@client-hub/store/store.feature-service'
import { VERSIONS as StoreVersions, ID as storeId } from '@client-hub/store/constants'
import { INTEGRATOR_ID, SERIALIZED_STATE_SCRIPT_TYPE } from 'constants/config'
import type { AppStore } from '@client-hub/store/createStore'
import { Persistor } from 'redux-persist'
import { defineSerializedStateManager } from '@feature-hub/serialized-state-manager'
import { CacheProvider } from '@emotion/react'
import createEmotionCache from 'utils/createEmotionCache'
import '@icanbwell/ui-toolkit/dist/index.css'

const { featureAppManager, featureServices } = createFeatureHub(INTEGRATOR_ID, {
  featureServiceDefinitions: [
    defineSerializedStateManager(
      document.querySelector(`script[type="${SERIALIZED_STATE_SCRIPT_TYPE}"]`)?.textContent || '',
    ),
    defineStoreFeatureService({ domain: window.location.hostname }),
  ],
  featureServiceDependencies: {
    [storeId]: StoreVersions.PRE_RELEASE,
  },
  moduleLoader: loadFederatedModule,
})

const cache = createEmotionCache()

const routes = [
  {
    // `App` renders `routeMap` itself via `useRoutes`, so it doesn't need to be declared again
    // as `children` here — `routeMap`'s entries use absolute paths (e.g. "/"), which react-router
    // v7 rejects as children of a wildcard parent. This outer route still needs a trailing "*"
    // so App's descendant `useRoutes()` call can match paths beyond "/" (v6 only warned about this).
    path: '/*',
    element: (
      <StrictMode>
        <FeatureHubContextProvider value={{ featureAppManager }}>
          <CacheProvider value={cache}>
            <App {...(featureServices[storeId] as { store: AppStore; persistor: Persistor })} />
          </CacheProvider>
        </FeatureHubContextProvider>
      </StrictMode>
    ),
  },
]
const router = createBrowserRouter(routes)

const rootEl = document.getElementById('root')!
// Local dev (webpack-dev-server) serves an empty `#root` with no SSR markup, unlike the
// production Express server in `integrator.node.tsx`. `hydrateRoot` on an empty root throws
// a hydration-mismatch error, so fall back to a plain client render when there's nothing to hydrate.
if (rootEl.hasChildNodes()) {
  hydrateRoot(rootEl, <RouterProvider router={router} />)
} else {
  createRoot(rootEl).render(<RouterProvider router={router} />)
}
